import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'
import {TURNSTILE_FAILURE_MESSAGE, useTurnstile} from '../core/useTurnstile'
import {useIntl} from 'react-intl'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'

const initialValues = {
  email: '',
}

export function ForgotPassword() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const {containerRef: turnstileRef, getToken: getCaptchaToken} = useTurnstile('forgot_password')

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.EMAIL'}))
      .max(50, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MAX_LENGTH'}, {max: '50'}))
      .required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      let captchaToken: string
      try {
        captchaToken = await getCaptchaToken()
      } catch {
        setErrorMessage(TURNSTILE_FAILURE_MESSAGE)
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        return
      }

      requestPassword(values.email, captchaToken)
        .then(({data}) => {
          setSuccessMessage(data.message || intl.formatMessage({id: 'AUTH.FORGOT.SENT_SUCCESS'}))
          setHasErrors(false)
          setLoading(false)
        })
        .catch((error) => {
          if (error.response?.status === 422 || error.response?.status === 429) {
            setErrorMessage(error.response.data.message)
          } else {
            setErrorMessage(intl.formatMessage({id: 'AUTH.FORGOT.SENT_FAIL'}))
          }

          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)
          setStatus('The login detail is incorrect')
        })
    },
  })

  return (
    <AuthAuroraShell>
      <AuthBrandHeader />

      {/*begin::Form*/}
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-8'>
          {/* begin::Title */}
          <h1 className='text-dark fw-bolder mb-3'>
            {intl.formatMessage({id: 'AUTH.FORGOT.TITLE'})}
          </h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-500 fw-semibold fs-6'>
            {intl.formatMessage({id: 'AUTH.FORGOT.DESC'})}
          </div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{errorMessage}</div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info'>{successMessage}</div>
          </div>
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>
            {intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}
          </label>
          <input
            type='email'
            placeholder=''
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* Turnstile: só aparece quando a Cloudflare pede interação */}
        <div ref={turnstileRef} className='d-flex justify-content-center mb-4' />

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
            <span className='indicator-label'>
              {intl.formatMessage({id: 'AUTH.GENERAL.SUBMIT_BUTTON'})}
            </span>
            {loading && (
              <span className='indicator-progress'>
                {intl.formatMessage({id: 'AUTH.GENERAL.LOADING'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn btn-light'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {intl.formatMessage({id: 'AUTH.GENERAL.CANCEL_BUTTON'})}
            </button>
          </Link>{' '}
        </div>
        {/* end::Form group */}
      </form>
      {/*end::Form*/}
    </AuthAuroraShell>
  )
}
